/* ---------- the Journey (12-week programme, Duolingo-style) ---------- */
/* Curated content + questions for learning to live well with your condition.
   Everything here is authored content (admin web → R2) — questions reinforce,
   they never test. No hearts, no lives, no punishment mechanics. */

export type QuestionType = 'choice' | 'truefalse';
export type LessonPart = {
  kind: 'content';
  heading: string;
  paragraphs: string[];
  narrated?: boolean;
} | {
  kind: 'question';
  type: QuestionType;
  prompt: string;
  options: string[];
  correctIndex: number;
  correctFeedback: string;
  incorrectFeedback: string;
};
export type StepStatus = 'done' | 'current' | 'locked';
export type MapStep = {
  id: string;
  title: string;
  minutes: number;
  status: StepStatus;
};
export type MapWeek = {
  week: number;
  title: string;
  steps: MapStep[];
};
export const JOURNEY = {
  programme: 'Living well with asthma',
  totalWeeks: 12,
  currentWeek: 7,
  streakDays: 5,
  sunnysNote: "You've logged your inhaler 5 days running — that's exactly what Week 7 builds on.",
  /* weeks 1–5 are collapsed into one summary row on the map */
  earlierWeeks: {
    label: 'Weeks 1–5 · The foundations',
    detail: '15 steps complete'
  },
  /* weeks 6–8 carry real steps; the rest are titles only */
  weeks: [{
    week: 6,
    title: 'Triggers at home',
    steps: [{
      id: 'dust-damp',
      title: 'Dust, damp and mould',
      minutes: 4,
      status: 'done'
    }, {
      id: 'pets-indoors',
      title: 'Pets and indoor pollen',
      minutes: 3,
      status: 'done'
    }, {
      id: 'preventer-routine',
      title: 'Your preventer routine',
      minutes: 5,
      status: 'done'
    }]
  }, {
    week: 7,
    title: 'Pollen season',
    steps: [{
      id: 'triggers-home',
      title: 'Triggers around the home',
      minutes: 4,
      status: 'done'
    }, {
      id: 'reading-forecast',
      title: 'Reading the pollen forecast',
      minutes: 3,
      status: 'done'
    }, {
      id: 'pollen-tactics',
      title: 'Pollen season tactics',
      minutes: 4,
      status: 'current'
    }, {
      id: 'pollen-caught-out',
      title: 'When pollen catches you out',
      minutes: 3,
      status: 'locked'
    }]
  }, {
    week: 8,
    title: 'Staying active',
    steps: [{
      id: 'exercise-airways',
      title: 'Exercise and your airways',
      minutes: 5,
      status: 'locked'
    }, {
      id: 'warm-up',
      title: 'Warming up the right way',
      minutes: 4,
      status: 'locked'
    }]
  }] as MapWeek[],
  /* weeks 9–12: locked, titles visible so the journey feels finite and purposeful */
  laterWeeks: [{
    week: 9,
    title: 'Reviewing your action plan'
  }, {
    week: 10,
    title: 'Sleep and your airways'
  }, {
    week: 11,
    title: 'Eating and drinking well'
  }, {
    week: 12,
    title: 'Your toolkit for life'
  }]
};

/* ---------- the current step's lesson ---------- */
/* "Pollen season tactics" — 5 parts, 2 already done.
   Resume drops straight back in at part 3. */

export const CURRENT_STEP = {
  id: 'pollen-tactics',
  title: 'Pollen season tactics',
  week: 7,
  weekTitle: 'Pollen season',
  minutes: 4,
  partsDone: 2,
  parts: [{
    kind: 'content',
    heading: 'Why pollen hits harder in summer',
    paragraphs: ['Grass pollen is the big one for most people with asthma in the UK — and early summer is when it peaks.', 'Warm, dry, breezy days carry the most pollen. It builds through the morning, peaks in the afternoon, and settles again by evening.'],
    narrated: true
  }, {
    kind: 'question',
    type: 'truefalse',
    prompt: 'Rainy days usually mean more pollen in the air.',
    options: ['True', 'False'],
    correctIndex: 1,
    correctFeedback: 'Right — rain actually washes pollen out of the air. Damp days are usually kinder days.',
    incorrectFeedback: 'It is the other way round — rain washes pollen out of the air, so damp days are usually kinder days.'
  }, {
    kind: 'content',
    heading: 'Your pollen game plan',
    paragraphs: ["Pollen season doesn't have to mean staying indoors. A few small habits make a real difference: check the forecast before you plan your day, take your preventer in the morning so it's already working, and keep your reliever with you.", 'On high-pollen days, sunglasses help keep pollen out of your eyes, and a shower when you get home washes it out of your hair before bed.', 'If grass pollen is your trigger, afternoons are the peak — mornings and evenings are usually kinder for being outside.'],
    narrated: true
  }, {
    kind: 'question',
    type: 'choice',
    prompt: 'Which of these is most likely to trigger asthma in pollen season?',
    options: ['Early morning dew', 'Grass pollen in the afternoon', 'A cool evening breeze', 'Light summer rain'],
    correctIndex: 1,
    correctFeedback: 'Exactly — grass pollen peaks in the afternoon, usually between 2 and 6pm.',
    incorrectFeedback: "Close — it's actually the afternoon grass-pollen peak that catches people out. Mornings and evenings are usually kinder."
  }, {
    kind: 'question',
    type: 'truefalse',
    prompt: 'On high-pollen days, it helps to take your preventer before you head out.',
    options: ['True', 'False'],
    correctIndex: 0,
    correctFeedback: "Spot on — your preventer protects best when it's already working before you meet the pollen.",
    incorrectFeedback: "It's actually true — a preventer works best taken before you go out, so it's already protecting your airways."
  }] as LessonPart[],
  /* shown on the step-complete screen */
  nextStep: {
    title: 'When pollen catches you out',
    minutes: 3
  }
};